import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { EditableFieldComponent } from 'components/input/custom-input/editable-field.component';
import { QuestionnaireComponent } from 'components/questionnaire/questionnaire.component';
import { TaskActionType } from 'enums/TaskActionType';
import { SiteModel } from 'models/generated/Distribution.Api.Models.Site';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { QuestionnaireModel } from 'models/generated/Entities.GormCrm.Questionnaire';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { Layout } from 'modules/layout';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription, forkJoin } from 'rxjs';
import { CompanyService } from 'services/company.service';
import { ComponentUrl, NavigationService } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { SiteService } from 'services/site.service';
import { TaskService } from 'services/task.service';
import { TaskActionLogComponent } from './task-action-log.component';
import { NewTaskActionComponent } from './new-task-action.component';
import { TaskflowTimelineComponent } from './taskflow-timeline.component';
import { ViewTaskActionComponent } from './view-task-action.component';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';

@Component({
	selector: 'default-taskflow',
	templateUrl: './default-taskflow.component.html',
	imports: [Layout, EditableFieldComponent, TaskflowTimelineComponent, QuestionnaireComponent, ViewTaskActionComponent, TaskActionLogComponent, NewTaskActionComponent, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('default-taskflow')
export class DefaultTaskflowComponent implements OnDestroy {

	questionnaires: { questionnaire: QuestionnaireModel, editable: boolean, showCategoryDetails: boolean }[] = [];
	allQuestionnairesAreValid = false;
	invalidQuestionnaireIds: string[] = [];

	private companySubscription?: Subscription;
	private taskflowSubscription?: Subscription;
	private _selectedTaskAction?: TaskActionModel;
	private _taskflow!: TaskflowModel;
	public loading = false;
	public orderLines: OrderLineModel[] = [];
	public productPlans: ProductPlanModel[] = [];
	public sites: SiteModel[] = [];

	company?: CompanyModel;
	newTimelineTaskAction?: TaskActionModel;
	newTaskAction?: TaskActionModel;
	creatingTaskActionId?: string;

	set selectedTaskAction(value: TaskActionModel | undefined) {

		if (value && this.taskService.isTriggerTask(value!)) return;

		this._selectedTaskAction = value;
		if (value) this.selectedActionIdChange.emit(value.id);
		this.navigationService.refreshUrl();
	}
	get selectedTaskAction() { return this._selectedTaskAction; }

	@Input()
	set taskflow(taskflow: TaskflowModel) {
		this._taskflow = taskflow;
		if (taskflow) {
			if (taskflow.companyId) {
				this.getCompany(taskflow.companyId!);
			}
			this.productPlans = taskflow.productPlans;
			this.orderLines = this.productPlans.flatMap(pp => pp.orderLines);
			this.siteService.getSitesByProductPlanIds(this.productPlans.map(pp => pp.id!)).then(sites => this.sites = sites);
		}
		if (this.taskflowSubscription) this.taskflowSubscription.unsubscribe();
		this.taskflowSubscription = this.pushService.onTaskflowChanges.subscribe(event => {
			const data = event.data;
			// Check if affected TaskAction doesn't exist in our list (reload taskflow and add missing action)
			if (data.taskflowId !== this.taskflow.id && !this.taskflow.subTaskflows.some(subTaskflow => subTaskflow.id === data.taskflowId)) return;

			this.taskService.getTaskflow(this.taskflow.id!).then(updatedTaskflow => {

				if (this.taskflow.subTaskflows.some(subTaskflow => subTaskflow.id === data.taskflowId) ||
					updatedTaskflow.subTaskflows.some(updatedSubTaskflow => this.taskflow.subTaskflows.every(subTaskflow => subTaskflow.id !== updatedSubTaskflow.id))) {
					this.taskflow.subTaskflows = updatedTaskflow.subTaskflows;
				}

				if (this.taskflow.title !== updatedTaskflow.title)
					this.taskflow.title = updatedTaskflow.title;

				if (this.taskflow.deadline !== updatedTaskflow.deadline)
					this.taskflow.deadline = updatedTaskflow.deadline;

				if (this.taskflow.taskActions !== updatedTaskflow.taskActions) {
					this.taskflow.taskActions = updatedTaskflow.taskActions;
					this.taskflow.currentStep = updatedTaskflow.currentStep;
					this.selectedTaskAction = updatedTaskflow.taskActions.find(taskAction => taskAction.id == this.selectedTaskAction!.id);
				} else if (data.taskActionId) {
					const taskAction = this.taskflow.taskActions.find(a => a.id === data.taskActionId);
					const updatedTaskAction = updatedTaskflow.taskActions.find(a => a.id === data.taskActionId);
					this.refreshDetailsOnTaskAction(taskAction, updatedTaskAction);
				}
			});
		});

		if (this.companySubscription) this.companySubscription.unsubscribe();
		this.companySubscription = this.pushService.onCompanyChanges.subscribe(event => {
			if (this.company && event.data.companyId === this.company.id) {
				this.getCompany(event.data.companyId);
			}
		});

	}
	get taskflow(): TaskflowModel {
		return this._taskflow;
	}

	@Input()
	set selectedActionId(actionId: string | undefined) {
		if (actionId && this.taskflow)
			this.selectedTaskAction = this.taskflow.taskActions.find(a => a.id === actionId);

		this.updateQuestionnaires();
	}
	@Output() selectedActionIdChange = new EventEmitter<string>();

	constructor(
		private companyService: CompanyService,
		public siteService: SiteService,
		private taskService: TaskService,
		private pushService: PushService,
		private navigationService: NavigationService,
		private questionnaireService: QuestionnaireService) {
		
	}

	addQuestionnaire(tag: string, editable: boolean, showCategoryDetails: boolean) {
		this.addQuestionnaires([
			{ tag: tag, editable: editable, showCategoryDetails: showCategoryDetails }
		]);
	}

	addQuestionnaires(questionnaireSettings: { tag: string, editable: boolean, showCategoryDetails: boolean }[]) {

		this.loading = true;

		forkJoin(questionnaireSettings.map(settings => {
			return this.questionnaireService.getQuestionnaireByTag(settings.tag).then(questionnaire => {
				return {
					questionnaire: questionnaire!,
					editable: settings.editable,
					showCategoryDetails: settings.showCategoryDetails,
				};
			});
		})).subscribe(questionnaireViews => {
			questionnaireViews.forEach(view => this.questionnaires.push(view));
			this.loading = false;
		});
	}

	updateQuestionnaires() {
		this.questionnaires = [];
		this.invalidQuestionnaireIds = [];
		this.allQuestionnairesAreValid = false;
		if (this.selectedTaskAction == undefined) return;

		if (this.taskService.isTaskflowCreatedFromTag(this.taskflow, 'onboarding.photo')) {
			this.addQuestionnaire('photo', false, false);
		}
		switch (this.selectedTaskAction.type) {
			case TaskActionType.qualificationCall:
				this.addQuestionnaire('ko', true, true);
				break;
			case TaskActionType.design:
				this.addQuestionnaires([
					{ tag: 'design', editable: false, showCategoryDetails: false },
					{ tag: 'design-notes', editable: true, showCategoryDetails: false }
				]);
				break;
			case TaskActionType.presentation:
				this.addQuestionnaire('design-notes', false, false);
				break;
			case TaskActionType.approval:
				this.addQuestionnaires([
					{ tag: 'design-notes', editable: false, showCategoryDetails: false },
					{ tag: 'design-approval', editable: true, showCategoryDetails: false }
				]);
				break;
			case TaskActionType.correction:
				if (this.taskflow.taskActions.some(taskAction => taskAction.type === TaskActionType.approval)) {
					this.addQuestionnaire('design-approval', false, false);
				}
				break;
			default:
				this.allQuestionnairesAreValid = true;
				break;
		}
	}

	updateTitle(title: string) {
		this.taskService.updateTaskflowTitle(this.taskflow.id!, title).then(() => this.taskflow.title = title);
	}

	createNewTaskActionFromTimeline(taskAction: TaskActionModel) {
		taskAction.taskflowId = this.taskflow.id;
		this.newTimelineTaskAction = taskAction;
	}

	createNewTaskAction(taskAction: TaskActionModel) {
		if (!this.selectedTaskAction) return;
		taskAction.taskflowId = this.taskflow.id;
		this.newTaskAction = taskAction;
		this.creatingTaskActionId = this.selectedTaskAction.id;
	}

	getCompany(companyId: string) {
		this.companyService.getCompany(companyId).then(company => {
			this.company = company;
		});
	}

	closeTaskAction() {
		if (this.creatingTaskActionId) {
			this.taskService.closeTaskAction(this.creatingTaskActionId, 'task closed and new task created').then(() => {
				this.newTaskAction = undefined;
				this.creatingTaskActionId = undefined;
			});
		} else {
			this.newTaskAction = undefined;
		}
	}

	private refreshDetailsOnTaskAction(taskAction?: TaskActionModel, updatedTaskAction?: TaskActionModel) {

		if (!updatedTaskAction) return;

		if (!taskAction) {
			// Task Action is missing entirely from list. Add it.
			this.taskflow.taskActions.push(updatedTaskAction);
			return;
		}

		// Task Action already exists in list. Check for changes we want to refresh
		if (updatedTaskAction.completed !== taskAction.completed)
			taskAction.completed = updatedTaskAction.completed;

		if (updatedTaskAction.details !== taskAction.details)
			taskAction.details = updatedTaskAction.details;

		if (updatedTaskAction.plannedDate !== taskAction.plannedDate)
			taskAction.plannedDate = updatedTaskAction.plannedDate;

		if (updatedTaskAction.assignedToUserId !== taskAction.assignedToUserId) {
			taskAction.assignedToUserId = updatedTaskAction.assignedToUserId;
		}

		if (updatedTaskAction.additionalData !== taskAction.additionalData) {
			taskAction.additionalData = updatedTaskAction.additionalData;
		}

		if (updatedTaskAction.taskAreaId !== taskAction.taskAreaId) {
			taskAction.taskAreaId = updatedTaskAction.taskAreaId;
			taskAction.taskArea = updatedTaskAction.taskArea;
		}
		taskAction.taskAttachments = updatedTaskAction.taskAttachments;

	}

	updateValidation(isValid: boolean, questionnaireId: string) {
		if (!this.questionnaires.find(q => q.questionnaire.id == questionnaireId)) return;

		const idIndex = this.invalidQuestionnaireIds.findIndex(id => id == questionnaireId);

		if (isValid && idIndex >= 0) {
			this.invalidQuestionnaireIds.splice(idIndex, 1);
		} else if (!isValid && idIndex == -1) {
			this.invalidQuestionnaireIds.push(questionnaireId);
		}

		this.allQuestionnairesAreValid = this.invalidQuestionnaireIds.length == 0;
	}

	openSite(site: SiteModel) {
		const primaryPointer = this.siteService.getSitePrimaryPointer(site);
		if (!primaryPointer) return;
		window.open(`https://${this.siteService.getSitePrimaryPointer(site)}`, '_blank');
	}

	ngOnDestroy() {
		if (this.taskflowSubscription) this.taskflowSubscription.unsubscribe();
		if (this.companySubscription) this.companySubscription.unsubscribe();
	}

}
